'use client'

import { type Locale, type TranslationValues, useI18n } from '@shared/config/i18n'
import { DEFAULT_TIMEZONE } from '@shared/libs/dayjs'
import React from 'react'
import { match, P } from 'ts-pattern'

import { BadgeV1, cn, Flex, Typography, VStack } from '@podo-app/design-system-temp'

import { formatCouponDate, formatCouponDiscountText, formatCouponMaxDiscountText } from '../../lib'
import { CouponModel } from '../../model'
import { CouponCardForPaymentProps } from '../../ui/coupon-card-for-payment/coupon-card-for-payment.types'
import { CouponCardProps, CouponCardTitleProps } from '../../ui/coupon-card/coupon-card.types'
import { CountDownTimeBadge, DdayBadge } from '../../ui/coupon-remaining-time-badge'

type Translate = (key: string, values?: TranslationValues) => string
type CouponI18nProps = { locale: Locale; t: Translate; currency?: string; nonApplicableReason?: string | null }

const CouponCardForPayment = (props: CouponCardForPaymentProps) => {
  const { effectiveLocale, t } = useI18n()
  const timezone = props.timezone ?? DEFAULT_TIMEZONE
  return (
    <VStack
      className={cn(
        'p-5 w-full gap-5 rounded-lg border-[1px]',
        props.applicable ? 'bg-white' : 'bg-gray-100 cursor-not-allowed',
      )}
    >
      {match(props)
        .with({ applicable: true }, (availableProps) => (
          <ApplicableForPaymentCouponCard {...availableProps} locale={effectiveLocale} timezone={timezone} t={t} />
        ))
        .with({ applicable: false }, (usedProps) => (
          <UnApplicableForPaymentCouponCard
            {...usedProps}
            locale={effectiveLocale}
            timezone={timezone}
            t={t}
            usedAt={null}
          />
        ))
        .exhaustive()}
    </VStack>
  )
}

const CouponCardContent = ({
  title,
  children,
  headerContent,
  ...props
}: {
  title: React.ReactNode
  children: React.ReactNode
  headerContent?: React.ReactNode
  isAvailable?: boolean
}) => (
  <VStack className="gap-2 items-start" {...props}>
    <Flex className="w-full justify-between items-center">{headerContent}</Flex>
    {title}
    <VStack>{children}</VStack>
  </VStack>
)

const ApplicableForPaymentCouponCard = (
  props: Omit<CouponCardProps & { status: typeof CouponModel.STATUS.NORMAL }, 'status'> &
    CouponI18nProps & { timezone: string },
) => {
  return (
    <CouponCardContent
      title={<Typography size="h3">{props.title}</Typography>}
      headerContent={
        <>
          <CouponCardTitle
            status={CouponModel.STATUS.NORMAL}
            currency={props.currency}
            discountType={props.discountType}
            discountAmount={props.discountAmount}
            isSubscribePayment={props.isSubscribePayment}
            locale={props.locale}
            t={props.t}
          />
          {match(props.remainingTimeType)
            .with('TIME', () => <CountDownTimeBadge endDate={props.useEndedAt} />)
            .with('DDAY', () => <DdayBadge endDate={props.useEndedAt} />)
            .with('NON_DISPLAY', () => null)
            .exhaustive()}
        </>
      }
    >
      <Typography size="body1" className="text-gray-400">
        {`${formatCouponDate(props.useStartedAt, props.timezone, props.locale)} ~ ${formatCouponDate(
          props.useEndedAt,
          props.timezone,
          props.locale,
        )}`}
      </Typography>
      {props.isPercentageDiscount && !props.isMaximumDiscount && (
        <Typography size="body1" className="text-gray-400">
          {formatCouponMaxDiscountText({
            discountAmountMax: props.discountAmountMax,
            currency: props.currency,
            isSubscribePayment: props.isSubscribePayment,
            locale: props.locale,
            t: props.t,
          })}
        </Typography>
      )}
    </CouponCardContent>
  )
}

const UnApplicableForPaymentCouponCard = (
  props: Omit<CouponCardProps & { status: typeof CouponModel.STATUS.USED }, 'status'> &
    CouponI18nProps & { timezone: string },
) => {
  return (
    <CouponCardContent
      title={
        <Typography size="h3" className="text-gray-400">
          {props.title}
        </Typography>
      }
      headerContent={
        <>
          <CouponCardTitle
            status={CouponModel.STATUS.USED}
            currency={props.currency}
            discountType={props.discountType}
            discountAmount={props.discountAmount}
            isSubscribePayment={props.isSubscribePayment}
            locale={props.locale}
            t={props.t}
          />
          <BadgeV1 className="px-2 flex gap-[5px] bg-gray-200 text-gray-400">
            {props.t('coupon.status.notApplicable')}
          </BadgeV1>
          {props.nonApplicableReason && (
            <Typography size="body2" className="text-gray-400">
              {props.nonApplicableReason}
            </Typography>
          )}
        </>
      }
    >
      <Typography size="body1" className="text-gray-400">
        {`${formatCouponDate(props.useStartedAt, props.timezone, props.locale)} ~ ${formatCouponDate(
          props.useEndedAt,
          props.timezone,
          props.locale,
        )}`}
      </Typography>
      {props.isPercentageDiscount && !props.isMaximumDiscount && (
        <Typography size="body1" className="text-gray-400">
          {formatCouponMaxDiscountText({
            discountAmountMax: props.discountAmountMax,
            currency: props.currency,
            isSubscribePayment: props.isSubscribePayment,
            locale: props.locale,
            t: props.t,
          })}
        </Typography>
      )}
    </CouponCardContent>
  )
}

const CouponCardTitle = ({
  status,
  currency,
  discountType,
  discountAmount,
  isSubscribePayment,
  locale,
  t,
}: CouponCardTitleProps & CouponI18nProps) => {
  const titleColor = match(status)
    .with(P.union(CouponModel.STATUS.NORMAL, CouponModel.STATUS.PENDING), () => 'text-green-500')
    .with(P.union(CouponModel.STATUS.USED, CouponModel.STATUS.EXPIRED), () => 'text-gray-400')
    .exhaustive()

  const discountText = formatCouponDiscountText({
    discountType,
    currency,
    discountAmount,
    isSubscribePayment,
    includeDiscountLabel: true,
    locale,
    t,
  })
  return <Typography className={`${titleColor} font-bold text-[22px]`}>{discountText}</Typography>
}

export default CouponCardForPayment
